import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Send, Mail, RotateCcw, Info } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Badges";
import { DigestPreview } from "@/components/admin/DigestPreview";

const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

const Row = ({ label, hint, children }) => (
  <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
    <div><p className="text-sm font-semibold">{label}</p>{hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}</div>
    <div className="shrink-0">{children}</div>
  </div>
);

const ActionButton = ({ onClick, busy, icon: Icon, children, testId, dark }) => (
  <button onClick={onClick} disabled={busy} data-testid={testId} className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${dark ? "bg-lasso-ink text-white hover:bg-lasso-navy" : "border border-lasso-line bg-white hover:border-lasso-ink"}`}>
    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {children}
  </button>
);

function EmailLog({ log }) {
  if (!log) return null;
  if (!log.items.length) return <EmptyState title="No emails sent yet" testId="email-log-empty" />;
  return (
    <div className="overflow-hidden rounded-2xl border border-lasso-line bg-white">
      <Table data-testid="email-log-table">
        <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Type</TableHead><TableHead>Recipient</TableHead><TableHead>Subject</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {log.items.map((e) => (
            <TableRow key={e.id} data-testid={`email-log-row-${e.id}`}>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmt(e.created_at)}</TableCell>
              <TableCell><span className="rounded-full bg-lasso-tint px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-lasso-navy">{e.type.replace("_", " ")}</span></TableCell>
              <TableCell className="text-xs"><p className="font-medium">{e.user_name}</p><p className="text-muted-foreground">{e.intended_to}{e.to !== e.intended_to ? ` → ${e.to}` : ""}</p></TableCell>
              <TableCell className="max-w-xs text-sm">{e.subject}</TableCell>
              <TableCell>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${e.status === "sent" ? "bg-emerald-50 text-emerald-700" : e.status === "skipped" ? "bg-amber-50 text-amber-800" : "bg-rose-50 text-rose-700"}`} data-testid={`email-log-status-${e.id}`}>{e.status}</span>
                {e.error && <p className={`mt-1 max-w-[220px] text-[11px] ${e.status === "skipped" ? "text-amber-700" : "text-rose-600"}`}>{e.error}</p>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function NotificationSettings() {
  const [s, setS] = useState(null);
  const [daysText, setDaysText] = useState("");
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(null);
  const [testTo, setTestTo] = useState("");

  const loadLog = useCallback(() => api.get("/admin/email-log").then((r) => setLog(r.data)), []);
  useEffect(() => {
    api.get("/admin/notification-settings").then((r) => { setS(r.data); setDaysText(r.data.days_before.join(", ")); });
    loadLog();
  }, [loadLog]);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy("save");
    try {
      const days_before = daysText.split(/[,\s]+/).map(Number).filter((n) => n > 0);
      const { data } = await api.put("/admin/notification-settings", { ...s, days_before });
      setS(data);
      setDaysText(data.days_before.join(", "));
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const action = async (key, path, method, message, body) => {
    setBusy(key);
    try {
      const { data } = await (body ? api[method](path, body) : api[method](path));
      toast.success(message(data));
      loadLog();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  if (!s) return null;
  return (
    <div className="fade-up">
      <PageHeader eyebrow="Admin" title="Notification settings" description="Due-date reminder emails, the weekly manager digest, and a log of every email sent." />
      <div className="mb-8 flex gap-3 rounded-xl border border-lasso-line bg-lasso-paper p-4 text-sm text-zinc-700" data-testid="email-sender-notice">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-lasso-deep" /> Emails are sent live as “{s.from_name}”. Demo accounts (marked “Demo” in Users) are never emailed — they're logged as skipped.
      </div>
      <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
        <div className="divide-y divide-lasso-line rounded-2xl border border-lasso-line bg-white" data-testid="notification-settings-form">
          <Row label="Reminders enabled" hint="Turns all reminder and digest emails on or off.">
            <Switch checked={s.enabled} onCheckedChange={(v) => set("enabled", v)} data-testid="notif-enabled-switch" />
          </Row>
          <Row label="Days before due date" hint="Comma-separated, e.g. 7, 3">
            <Input className="w-40" value={daysText} onChange={(e) => setDaysText(e.target.value)} data-testid="notif-days-before-input" />
          </Row>
          <Row label="On the due date" hint="Send a reminder the day an item is due.">
            <Switch checked={s.due_day} onCheckedChange={(v) => set("due_day", v)} data-testid="notif-due-day-switch" />
          </Row>
          <Row label="Repeat while overdue (days)" hint="0 = send only one overdue reminder.">
            <Input type="number" min={0} max={60} className="w-24" value={s.overdue_interval_days} onChange={(e) => set("overdue_interval_days", Number(e.target.value))} data-testid="notif-overdue-interval-input" />
          </Row>
          <Row label="Weekly manager digest" hint="Regional & Community Managers get a summary of overdue staff in their scope.">
            <Switch checked={s.digest_enabled} onCheckedChange={(v) => set("digest_enabled", v)} data-testid="notif-digest-switch" />
          </Row>
          <Row label="Digest day">
            <Select value={String(s.digest_day)} onValueChange={(v) => set("digest_day", Number(v))}>
              <SelectTrigger className="w-40" data-testid="notif-digest-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>{s.weekdays.map((d, i) => <SelectItem key={d} value={String(i)} data-testid={`notif-digest-day-${i}`}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          <div className="p-5"><ActionButton onClick={save} busy={busy === "save"} icon={Mail} dark testId="notif-save-button">Save settings</ActionButton></div>
        </div>
        <aside className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-400">Run now</p>
          <p className="text-sm text-muted-foreground">Reminders also run automatically every hour. Each assignment + stage is emailed once.</p>
          <div className="flex flex-col items-start gap-2">
            <ActionButton onClick={() => action("run", "/admin/notifications/run", "post", (d) => d.enabled ? `Reminders: ${d.sent} sent, ${d.skipped} skipped (demo), ${d.failed} failed, ${d.already_sent} already handled` : "Reminders are disabled")} busy={busy === "run"} icon={Play} dark testId="notif-run-now-button">Run reminders now</ActionButton>
            <ActionButton onClick={() => action("digest", "/admin/notifications/run-digest", "post", (d) => `Digest: ${d.sent} sent, ${d.skipped} skipped (demo), ${d.failed} failed, ${d.skipped_no_overdue} managers with nothing overdue`)} busy={busy === "digest"} icon={Send} testId="notif-run-digest-button">Send manager digest now</ActionButton>
            <DigestPreview />
            <div className="mt-4 w-full rounded-xl border border-lasso-line bg-white p-3">
              <p className="mb-2 text-xs font-semibold">Send test email</p>
              <div className="flex gap-2">
                <Input type="email" placeholder="you@company.com" value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="notif-test-email-input" />
                <ActionButton onClick={() => action("test", "/admin/notifications/test", "post", (d) => d.status === "sent" ? `Test email sent to ${d.to}` : `Test ${d.status}: ${d.error}`, { to: testTo })} busy={busy === "test"} icon={Mail} testId="notif-send-test-button">Send</ActionButton>
              </div>
            </div>
            <ActionButton onClick={() => window.confirm("Clear the sent-reminder history? The next run will email every due/overdue item again.") && action("reset", "/admin/notifications/reminder-history", "delete", (d) => `Cleared ${d.deleted} reminder records`)} busy={busy === "reset"} icon={RotateCcw} testId="notif-reset-history-button">Reset reminder history</ActionButton>
          </div>
        </aside>
      </div>
      <section className="mt-14">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-sm font-bold uppercase tracking-[0.14em]">Email log</h2>
          {log && <p className="text-xs text-muted-foreground" data-testid="email-log-counts">{log.counts.sent} sent · {log.counts.skipped} skipped · {log.counts.failed} failed</p>}
        </div>
        <EmailLog log={log} />
      </section>
    </div>
  );
}
